/**
 * Hero training: the calibration step machine (CAM-02, GDS §13, architecture §20).
 *
 * GDS §13 lists seven steps — stand in lane, neutral pose, raise both hands, crouch, move within
 * lane, jump or substitute, confirm — and says the result is stored session-only. This is that
 * sequence as a machine: each step has an entry criterion, a capture rule and a piece of feedback,
 * and the sequence ends in a {@link CalibrationProfile} whose `baselineMeasurements` are what every
 * detector then reads.
 *
 * **Why measured thresholds and not constants.** Slice B's `DEFAULT_POSE_CALIBRATION` is a
 * plausible average five-year-old; a real one is small, or tall, or standing closer to the camera
 * than the average, and a crouch threshold set for the average tells half of them that they did not
 * crouch. Every threshold here is expressed as a multiple of the child's own body scale, taken from
 * their own neutral pose, and set at a *fraction* of what they demonstrated — a threshold at their
 * best effort would only ever fire on their best effort.
 *
 * **What it deliberately does not do.** It has no camera (frames arrive from a {@link PoseTracker}
 * or, in tests, one at a time), no storage, no narration and no mission. It never fabricates a
 * measurement: a step the child could not do is *skipped*, its capability is left out of the
 * profile, and the quality figure drops — so a consumer substitutes rather than trusting a number
 * nobody measured (GDS §15's substitute set exists for exactly this).
 *
 * **Timing comes from frames, not from a clock.** Every hold is measured against `frame.detectedAt`,
 * so the same synthetic sequence produces the same result on any machine, and a slow model cannot
 * make a child hold a pose for longer than they were asked to.
 */

import {
  CALIBRATION_STEP_LABELS,
  CALIBRATION_TUNABLES,
  DEFAULT_CALIBRATION_CAMERA_MODE,
  type CalibrationTunables,
} from '../../platform/config/calibration';
import type { CalibrationLane } from '../../platform/config/cooperative-calibration';
import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { InputCapability } from '../../shared/types/capabilities';
import type { GameplayTunables } from '../../shared/types/tunables';
import type { Viewport } from '../../shared/types/presentation';
import type { CameraMode } from '../camera/readiness';
import { createObservationContinuity } from '../pose/observation-continuity';
import { DEFAULT_POSE_CALIBRATION } from '../pose/pose-calibration';
import {
  hipMidpoint,
  mirroredX,
  primaryPose,
  shoulderMidpoint,
  torsoCentre,
} from '../pose/pose-geometry';
import { POSE_LANDMARK, type PoseFrame, type PoseObservation } from '../pose/pose-types';
import {
  baselineMeasurementsFrom,
  deriveQuality,
  type CalibrationProfile,
} from './calibration-profile';

export { CALIBRATION_STEP_LABELS };
export type { CalibrationLane };

export type CalibrationStepId =
  | 'stand-in-lane'
  | 'neutral-pose'
  | 'raise-hands'
  | 'crouch'
  | 'move-in-lane'
  | 'jump'
  | 'reach'
  | 'confirm';

export type CalibrationStepStatus = 'pending' | 'active' | 'captured' | 'skipped';

/**
 * What the screen says about the step in progress.
 *
 * A code rather than a sentence: the words live in the config layer with the rest of the copy, and
 * a test can assert what the machine believes without asserting what a child reads.
 */
export type CalibrationFeedback =
  | 'WAITING_FOR_BODY'
  | 'LOW_VISIBILITY'
  | 'OUT_OF_LANE'
  | 'HOLD_STILL'
  | 'SHOW_THE_MOVE'
  | 'NEEDS_BIGGER_MOVE'
  | 'MOVE_SEEN'
  | 'CAPTURED'
  | 'CONFIRM';

export type CalibrationStatus = 'idle' | 'running' | 'complete' | 'cancelled';

export interface CalibrationStepState {
  readonly id: CalibrationStepId;
  readonly label: string;
  readonly status: CalibrationStepStatus;
  readonly feedback: CalibrationFeedback;
  /** How far through this step's capture the child is, 0–1. */
  readonly progress: number;
  /** What measuring this step proves the child can do, if anything. */
  readonly capability: InputCapability | null;
  readonly skippable: boolean;
}

export interface CalibrationFlowState {
  readonly status: CalibrationStatus;
  readonly cameraMode: CameraMode;
  readonly steps: readonly CalibrationStepState[];
  readonly activeStepId: CalibrationStepId | null;
  /** How well the child can be seen right now, 0–1. Zero means nobody is in frame. */
  readonly visibility: number;
  readonly profile: CalibrationProfile | null;
}

/** What the flow needs of a {@link PoseTracker}: frames, and a way to stop listening. */
export interface CalibrationFrameSource {
  subscribe(listener: (frame: PoseFrame) => void): () => void;
}

export interface CalibrationFlowOptions {
  readonly playerId: string;
  readonly viewport: Viewport;
  readonly cameraDeviceId?: string | undefined;
  /** GDS §6's play-space mode. Full-Motion by default; the selection UI is Slice F (D-059). */
  readonly cameraMode?: CameraMode | undefined;
  /** The half of the picture this hero trains in, when there are two of them (D-079(4)). */
  readonly lane?: CalibrationLane | undefined;
  readonly frames?: CalibrationFrameSource | null | undefined;
  readonly tunables?: GameplayTunables | undefined;
  readonly config?: CalibrationTunables | undefined;
  /** Wall clock, used only for `createdAt`. All timing comes from the frames themselves. */
  readonly now?: (() => number) | undefined;
}

export interface CalibrationFlow {
  readonly state: CalibrationFlowState;
  start(): void;
  /** Reads one frame. The tracker calls this; tests call it directly. */
  observe(frame: PoseFrame): void;
  /** Moves past a move the child cannot do today. Refused on the steps everything depends on. */
  skipStep(): void;
  /** The child's answer to step 7. */
  confirm(): void;
  restart(): void;
  cancel(): void;
  attach(): void;
  detach(): void;
  subscribe(listener: () => void): () => void;
}

type StepKind = 'steady' | 'action' | 'lane' | 'confirm';
type ActionKind = 'hands' | 'crouch' | 'jump' | 'reach';

interface StepSpec {
  readonly id: CalibrationStepId;
  readonly kind: StepKind;
  readonly capability: InputCapability | null;
  readonly skippable: boolean;
  readonly holdMs?: number;
  readonly action?: ActionKind;
  /** The child must be inside the lane guide for this step to count. */
  readonly requiresLaneGuide?: boolean;
}

function stepSpecs(config: CalibrationTunables, cameraMode: CameraMode): readonly StepSpec[] {
  const sixth: StepSpec =
    cameraMode === 'compact'
      ? // Compact substitutes the jump entirely (GDS §15). The reach is measured, but JUMP is not
        // a capability this sitting can claim — and saying otherwise is how a child gets asked for
        // something they were never calibrated for.
        {
          id: 'reach',
          kind: 'action',
          capability: 'HANDS_RAISED',
          skippable: true,
          action: 'reach',
        }
      : { id: 'jump', kind: 'action', capability: 'JUMP', skippable: true, action: 'jump' };

  return [
    {
      id: 'stand-in-lane',
      kind: 'steady',
      capability: 'PLAYER_PRESENCE',
      skippable: false,
      holdMs: config.steadyHoldMs,
      requiresLaneGuide: true,
    },
    {
      id: 'neutral-pose',
      kind: 'steady',
      capability: 'PLAYER_PRESENCE',
      skippable: false,
      holdMs: config.neutralHoldMs,
    },
    {
      id: 'raise-hands',
      kind: 'action',
      capability: 'HANDS_RAISED',
      skippable: true,
      action: 'hands',
    },
    { id: 'crouch', kind: 'action', capability: 'CROUCH', skippable: true, action: 'crouch' },
    { id: 'move-in-lane', kind: 'lane', capability: 'BODY_ZONE', skippable: true },
    sixth,
    { id: 'confirm', kind: 'confirm', capability: null, skippable: false },
  ];
}

interface StepRuntime {
  status: CalibrationStepStatus;
  feedback: CalibrationFeedback;
  progress: number;
  /** Confidences seen while the capture was running. */
  confidences: number[];
  holdStartedAt: number | null;
  samples: number;
  /** Steady steps: the readings being averaged. */
  hipY: number[];
  shoulderY: number[];
  torsoX: number[];
  torsoY: number[];
  /** Action steps: the largest movement seen so far, in body scales. */
  peak: number;
  /** Lane step: how far the child has explored, in mirrored x. */
  minX: number;
  maxX: number;
}

function freshRuntime(): StepRuntime {
  return {
    status: 'pending',
    feedback: 'WAITING_FOR_BODY',
    progress: 0,
    confidences: [],
    holdStartedAt: null,
    samples: 0,
    hipY: [],
    shoulderY: [],
    torsoX: [],
    torsoY: [],
    peak: 0,
    minX: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
  };
}

function median(values: readonly number[]): number {
  // Median rather than mean: one frame where the model put a hip through the floor should not move
  // the number every other threshold is a multiple of.
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length === 0) return 0;
  if (sorted.length % 2 === 1) return sorted[middle] ?? 0;
  return ((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Measurements accumulated across the sitting. */
interface Working {
  standingHipY?: number;
  bodyScale?: number;
  torsoMidpointX?: number;
  torsoMidpointY?: number;
  handsRaiseScale?: number;
  crouchDropScale?: number;
  jumpRiseScale?: number;
  substituteReachScale?: number;
  laneLeftMax?: number;
  laneRightMin?: number;
  laneHysteresis?: number;
}

const WRISTS = [POSE_LANDMARK.leftWrist, POSE_LANDMARK.rightWrist];

/** How far the highest wrist clears the shoulder line. Negative while the arms are down. */
function wristClearance(pose: PoseObservation, shoulderY: number): number {
  let best = Number.NEGATIVE_INFINITY;
  for (const index of WRISTS) {
    const wrist = pose.landmarks[index];
    if (wrist === undefined) continue;
    // Frame space grows downward: a raised wrist is above the shoulders, so smaller y.
    best = Math.max(best, shoulderY - wrist.y);
  }
  return Number.isFinite(best) ? best : 0;
}

export function createCalibrationFlow(options: CalibrationFlowOptions): CalibrationFlow {
  const config = options.config ?? CALIBRATION_TUNABLES;
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const cameraMode = options.cameraMode ?? DEFAULT_CALIBRATION_CAMERA_MODE;
  const now = options.now ?? (() => Date.now());
  const specs = stepSpecs(config, cameraMode);
  const lane = options.lane;

  /**
   * Where the glowing ring is, and how wide it is.
   *
   * A solo child stands in the middle of the picture. A cooperative hero stands in the middle of
   * *their half* — asking them for the middle of the frame would put both children on the midline,
   * which is precisely the arrangement the lane resolver cannot read.
   */
  const laneGuide =
    lane === undefined
      ? { centre: 0.5, halfWidth: config.laneGuideHalfWidth }
      : {
          centre: (lane.from + lane.to) / 2,
          halfWidth: Math.min(config.laneGuideHalfWidth, (lane.to - lane.from) / 2),
        };

  /** A cooperative hero's portals stay in their own half, however far they overshoot. */
  const withinLane = (x: number): number =>
    lane === undefined ? x : Math.max(lane.from, Math.min(lane.to, x));

  const listeners = new Set<() => void>();
  let unsubscribe: (() => void) | null = null;

  let status: CalibrationStatus = 'idle';
  let index = -1;
  let visibility = 0;
  let profile: CalibrationProfile | null = null;
  let working: Working = {};
  let runtimes = specs.map(() => freshRuntime());
  /** Whether this flow is inside a run of frames it was actually shown (S5-9, D-090). */
  const continuity = createObservationContinuity(tunables);
  let snapshot: CalibrationFlowState | null = null;

  const notify = (): void => {
    snapshot = null;
    for (const listener of [...listeners]) listener();
  };

  const activeSpec = (): StepSpec | null => specs[index] ?? null;
  const activeRuntime = (): StepRuntime | null => runtimes[index] ?? null;

  const setFeedback = (runtime: StepRuntime, feedback: CalibrationFeedback): void => {
    if (runtime.feedback === feedback) return;
    runtime.feedback = feedback;
    notify();
  };

  const setProgress = (runtime: StepRuntime, progress: number): void => {
    const next = round2(Math.max(0, Math.min(1, progress)));
    if (runtime.progress === next) return;
    runtime.progress = next;
    notify();
  };

  /** Everything a step was part-way through, forgotten. */
  const resetProgress = (runtime: StepRuntime): void => {
    runtime.holdStartedAt = null;
    runtime.samples = 0;
    runtime.hipY = [];
    runtime.shoulderY = [];
    runtime.torsoX = [];
    runtime.torsoY = [];
    runtime.peak = 0;
    runtime.minX = Number.POSITIVE_INFINITY;
    runtime.maxX = Number.NEGATIVE_INFINITY;
    runtime.confidences = [];
    setProgress(runtime, 0);
  };

  const advance = (): void => {
    index += 1;
    const runtime = activeRuntime();
    if (runtime === null) return;
    runtime.status = 'active';
    runtime.feedback = specs[index]?.kind === 'confirm' ? 'CONFIRM' : 'WAITING_FOR_BODY';
    notify();
  };

  const capture = (runtime: StepRuntime): void => {
    runtime.status = 'captured';
    runtime.feedback = 'CAPTURED';
    runtime.progress = 1;
    advance();
  };

  /** Clamped so that one odd capture cannot make every twitch — or nothing at all — an answer. */
  const scaleFrom = (peak: number): number => {
    const { min, max } = config.scaleBounds;
    return Math.max(min, Math.min(max, peak * config.thresholdFraction));
  };

  function observeSteady(
    spec: StepSpec,
    runtime: StepRuntime,
    pose: PoseObservation,
    at: number,
    confidence: number,
  ): void {
    const hips = hipMidpoint(pose);
    const shoulders = shoulderMidpoint(pose);
    const torso = torsoCentre(pose);
    if (hips === null || shoulders === null || torso === null) return;

    if (spec.requiresLaneGuide === true) {
      const x = mirroredX(torso.x);
      if (Math.abs(x - laneGuide.centre) > laneGuide.halfWidth) {
        resetProgress(runtime);
        setFeedback(runtime, 'OUT_OF_LANE');
        return;
      }
    }

    if (runtime.holdStartedAt === null) runtime.holdStartedAt = at;
    runtime.samples += 1;
    runtime.hipY.push(hips.y);
    runtime.shoulderY.push(shoulders.y);
    runtime.torsoX.push(mirroredX(torso.x));
    runtime.torsoY.push(torso.y);
    runtime.confidences.push(confidence);

    const holdMs = spec.holdMs ?? config.steadyHoldMs;
    const held = at - runtime.holdStartedAt;
    setFeedback(runtime, 'HOLD_STILL');
    setProgress(runtime, holdMs === 0 ? 1 : held / holdMs);

    if (held < holdMs || runtime.samples < config.minSamples) return;

    if (spec.id === 'neutral-pose') {
      const standingHipY = median(runtime.hipY);
      const bodyScale = Math.abs(standingHipY - median(runtime.shoulderY));
      // A body with no measurable torso is not a calibration; keep waiting rather than dividing
      // every later threshold by nothing.
      if (bodyScale <= 0) {
        resetProgress(runtime);
        return;
      }
      working = {
        ...working,
        standingHipY,
        bodyScale,
        torsoMidpointX: median(runtime.torsoX),
        torsoMidpointY: median(runtime.torsoY),
      };
    }

    capture(runtime);
  }

  function magnitudeFor(action: ActionKind, pose: PoseObservation): number | null {
    const hips = hipMidpoint(pose);
    const shoulders = shoulderMidpoint(pose);
    if (hips === null || shoulders === null) return null;

    const standingHipY = working.standingHipY ?? hips.y;

    switch (action) {
      case 'hands':
      case 'reach':
        return wristClearance(pose, shoulders.y);
      case 'crouch':
        return hips.y - standingHipY;
      case 'jump':
        return standingHipY - hips.y;
      default:
        return null;
    }
  }

  function observeAction(
    spec: StepSpec,
    runtime: StepRuntime,
    pose: PoseObservation,
    confidence: number,
  ): void {
    const action = spec.action;
    const bodyScale = working.bodyScale;
    if (action === undefined || bodyScale === undefined || bodyScale <= 0) return;

    const raw = magnitudeFor(action, pose);
    if (raw === null) return;

    const normalised = raw / bodyScale;
    const minimum = config.minimumMoveScale[action];

    if (normalised >= minimum) {
      runtime.peak = Math.max(runtime.peak, normalised);
      runtime.samples += 1;
      runtime.confidences.push(confidence);
      setFeedback(runtime, 'MOVE_SEEN');
      setProgress(runtime, runtime.samples / config.minSamples);
      return;
    }

    // The move has finished: the body has come back from a peak worth measuring.
    if (runtime.peak >= minimum && runtime.samples >= config.minSamples) {
      if (normalised <= runtime.peak * config.returnFraction) {
        applyActionMeasurement(action, runtime.peak);
        capture(runtime);
        return;
      }
      setFeedback(runtime, 'MOVE_SEEN');
      return;
    }

    setFeedback(
      runtime,
      normalised >= minimum * config.noticeFraction ? 'NEEDS_BIGGER_MOVE' : 'SHOW_THE_MOVE',
    );
  }

  function applyActionMeasurement(action: ActionKind, peak: number): void {
    const scale = scaleFrom(peak);
    if (action === 'hands') working = { ...working, handsRaiseScale: scale };
    if (action === 'crouch') working = { ...working, crouchDropScale: scale };
    if (action === 'jump') working = { ...working, jumpRiseScale: scale };
    if (action === 'reach') working = { ...working, substituteReachScale: scale };
  }

  function observeLane(runtime: StepRuntime, pose: PoseObservation, confidence: number): void {
    const torso = torsoCentre(pose);
    if (torso === null) return;

    const x = withinLane(mirroredX(torso.x));
    runtime.minX = Math.min(runtime.minX, x);
    runtime.maxX = Math.max(runtime.maxX, x);
    runtime.samples += 1;
    runtime.confidences.push(confidence);

    const range = runtime.maxX - runtime.minX;
    setProgress(runtime, range / config.minLaneTravel);

    if (range < config.minLaneTravel) {
      setFeedback(
        runtime,
        range >= config.minLaneTravel * config.noticeFraction
          ? 'NEEDS_BIGGER_MOVE'
          : 'SHOW_THE_MOVE',
      );
      return;
    }

    if (runtime.samples < config.minSamples) {
      setFeedback(runtime, 'MOVE_SEEN');
      return;
    }

    // Back in the middle of what they explored: three lanes can be drawn around them.
    const centre = (runtime.minX + runtime.maxX) / 2;
    if (Math.abs(x - centre) > range * 0.25) {
      setFeedback(runtime, 'MOVE_SEEN');
      return;
    }

    const lane = range / 3;
    working = {
      ...working,
      laneLeftMax: runtime.minX + lane,
      laneRightMin: runtime.maxX - lane,
      laneHysteresis: lane * config.laneHysteresisFraction,
    };
    capture(runtime);
  }

  /**
   * The portals a child who skipped the sideways step gets.
   *
   * Whole-frame for a solo child — the shipped Slice B placeholder, unchanged. Equal thirds of
   * their own half for a cooperative hero, because whole-frame bounds in a two-hero sitting read
   * one child as permanently in the right-hand portal and the other permanently in the left
   * (D-079(4)).
   */
  function fallbackLane(): { leftMax: number; rightMin: number; hysteresis: number } {
    if (lane === undefined) {
      return {
        leftMax: DEFAULT_POSE_CALIBRATION.laneBounds.leftMax,
        rightMin: DEFAULT_POSE_CALIBRATION.laneBounds.rightMin,
        hysteresis: DEFAULT_POSE_CALIBRATION.laneHysteresis,
      };
    }
    const third = (lane.to - lane.from) / 3;
    return {
      leftMax: lane.from + third,
      rightMin: lane.to - third,
      hysteresis: third * config.laneHysteresisFraction,
    };
  }

  function buildProfile(): CalibrationProfile {
    const fallback = fallbackLane();
    const measurements = baselineMeasurementsFrom(
      {
        standingHipY: working.standingHipY ?? DEFAULT_POSE_CALIBRATION.standingHipY,
        bodyScale: working.bodyScale ?? DEFAULT_POSE_CALIBRATION.bodyScale,
        laneBounds: {
          leftMax: working.laneLeftMax ?? fallback.leftMax,
          rightMin: working.laneRightMin ?? fallback.rightMin,
        },
        laneHysteresis: working.laneHysteresis ?? fallback.hysteresis,
        jumpRiseScale: working.jumpRiseScale ?? DEFAULT_POSE_CALIBRATION.jumpRiseScale,
        crouchDropScale: working.crouchDropScale ?? DEFAULT_POSE_CALIBRATION.crouchDropScale,
        handsRaiseScale: working.handsRaiseScale ?? DEFAULT_POSE_CALIBRATION.handsRaiseScale,
        // Measured by Compact's step 6 only (GDS §15). A Full-Motion profile keeps the flagged
        // placeholder, and its `supportedCapabilities` is what tells a consumer which is which.
        substituteReachScale:
          working.substituteReachScale ?? DEFAULT_POSE_CALIBRATION.substituteReachScale,
        // Not measured by any GDS §13 step, and left at the flagged Slice B placeholders on
        // purpose: how many hands count as an answer, how still is still, and how much of a
        // threshold must be released are rules about the game, not about this child's body.
        handsRaisedWristCount: DEFAULT_POSE_CALIBRATION.handsRaisedWristCount,
        stillnessSpeedScale: DEFAULT_POSE_CALIBRATION.stillnessSpeedScale,
        hysteresisFraction: DEFAULT_POSE_CALIBRATION.hysteresisFraction,
      },
      {
        ...(working.torsoMidpointX === undefined ? {} : { torsoMidpointX: working.torsoMidpointX }),
        ...(working.torsoMidpointY === undefined ? {} : { torsoMidpointY: working.torsoMidpointY }),
      },
    );

    const capabilities: InputCapability[] = [];
    const confidences: number[] = [];
    let measurableSteps = 0;

    specs.forEach((spec, position) => {
      if (spec.capability === null) return;
      measurableSteps += 1;

      const runtime = runtimes[position];
      if (runtime?.status !== 'captured') return;

      confidences.push(
        runtime.confidences.length === 0
          ? 0
          : runtime.confidences.reduce((total, value) => total + value, 0) /
              runtime.confidences.length,
      );
      if (!capabilities.includes(spec.capability)) capabilities.push(spec.capability);
    });

    return {
      playerId: options.playerId,
      ...(options.cameraDeviceId === undefined ? {} : { cameraDeviceId: options.cameraDeviceId }),
      viewport: options.viewport,
      // Spider Math is an AR_CAMERA module (architecture §5); Compact/Full-Motion is the separate
      // `cameraMode` axis, because §5's modes describe a game and §6's describe a room.
      presentationMode: 'AR_CAMERA',
      cameraMode,
      supportedCapabilities: capabilities,
      baselineMeasurements: measurements,
      quality: deriveQuality({ confidences, measurableSteps }),
      createdAt: new Date(now()).toISOString(),
    };
  }

  const flow: CalibrationFlow = {
    get state(): CalibrationFlowState {
      if (snapshot === null) {
        snapshot = {
          status,
          cameraMode,
          steps: specs.map((spec, position) => {
            const runtime = runtimes[position] ?? freshRuntime();
            return {
              id: spec.id,
              label: CALIBRATION_STEP_LABELS[spec.id],
              status: runtime.status,
              feedback: runtime.feedback,
              progress: runtime.progress,
              capability: spec.capability,
              skippable: spec.skippable,
            };
          }),
          activeStepId: activeSpec()?.id ?? null,
          visibility,
          profile,
        };
      }
      return snapshot;
    },

    start(): void {
      if (status === 'running') return;
      status = 'running';
      index = -1;
      profile = null;
      working = {};
      runtimes = specs.map(() => freshRuntime());
      // A fresh sitting starts a run of frames rather than continuing the last one's.
      continuity.forget();
      advance();
      notify();
    },

    observe(frame: PoseFrame): void {
      if (status !== 'running') return;

      const spec = activeSpec();
      const runtime = activeRuntime();
      if (spec === null || runtime === null) return;

      /**
       * **A hold is a claim about watched time** (S5-9, D-090).
       *
       * Every step here is timed from the frames themselves, which is right — a wall clock would let
       * a busy machine invent a hold. What the subtraction alone could not see is the frames that
       * never came: three samples and a hole completed an 800 ms hold nobody watched 800 ms of, and
       * the child could have wandered off, crouched or turned side-on inside it. The profile that
       * comes out decides whether their crouch registers for the rest of the mission.
       *
       * So an unobserved interval throws away what the step was part-way through — the same
       * `resetProgress` a dim frame or a step out of the lane guide already causes, and for the same
       * reason: none of them is a measurement. It fails in the right direction, too. A family repeats
       * a step a grown-up is standing over; the alternative is a child measured from a stretch
       * nobody saw.
       */
      if (continuity.note(frame.detectedAt)) {
        resetProgress(runtime);
        setFeedback(runtime, 'WAITING_FOR_BODY');
      }

      const pose = primaryPose(frame);
      const confidence = pose?.coreConfidence ?? 0;

      const nextVisibility = round2(confidence);
      if (nextVisibility !== visibility) {
        visibility = nextVisibility;
        notify();
      }

      if (spec.kind === 'confirm') {
        setFeedback(runtime, 'CONFIRM');
        return;
      }

      if (pose === null) {
        resetProgress(runtime);
        setFeedback(runtime, 'WAITING_FOR_BODY');
        return;
      }

      if (confidence < tunables.pose.minCoreConfidence) {
        // A body we cannot see well is not a measurement. Nothing is recorded, and nothing about
        // the child is blamed for it (MVP notes §13).
        resetProgress(runtime);
        setFeedback(runtime, 'LOW_VISIBILITY');
        return;
      }

      if (spec.kind === 'steady') {
        observeSteady(spec, runtime, pose, frame.detectedAt, confidence);
        return;
      }
      if (spec.kind === 'action') {
        observeAction(spec, runtime, pose, confidence);
        return;
      }
      observeLane(runtime, pose, confidence);
    },

    skipStep(): void {
      if (status !== 'running') return;
      const spec = activeSpec();
      const runtime = activeRuntime();
      if (spec === null || runtime === null) return;
      // The neutral pose is the unit every other threshold is expressed in, and the lane guide is
      // what proves there is a child there at all. Skipping either would leave a profile of
      // measurements with nothing to measure against.
      if (!spec.skippable) return;

      runtime.status = 'skipped';
      runtime.progress = 0;
      advance();
    },

    confirm(): void {
      if (status !== 'running') return;
      const spec = activeSpec();
      const runtime = activeRuntime();
      if (spec === null || runtime === null || spec.kind !== 'confirm') return;

      runtime.status = 'captured';
      runtime.progress = 1;
      profile = buildProfile();
      status = 'complete';
      notify();
    },

    restart(): void {
      status = 'idle';
      flow.start();
    },

    cancel(): void {
      status = 'cancelled';
      profile = null;
      working = {};
      notify();
    },

    attach(): void {
      if (unsubscribe !== null) return;
      unsubscribe = options.frames?.subscribe((frame) => flow.observe(frame)) ?? null;
    },

    detach(): void {
      unsubscribe?.();
      unsubscribe = null;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };

  return flow;
}
